"use client";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import { usePathname } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { logout, updateUserProfile, mobileVerified, updateOnlineStatus } from "@/redux/slices/authSlice";
import {
  Menu as MenuIcon,
  User,
  Layers,
  Megaphone,
  Brush,
  Camera,
  Image as ImageIcon,
  ChevronDown,
  CheckCircle2,
  Settings,
  LogOut,
  FilePlus,
  MessageCircle,
  Wifi,
  WifiOff,
  Phone,
  Grid,
} from "lucide-react";
import Logo from "../../../../public/images/Hyrelancer2.png";
import Explor from "../../../../public/images/explorhyre.png";
import Service from "../../../../public/images/service.png";
import Contact from "../../../../public/images/contact.png";
import Catagery from "../../../../public/images/categories.png";
import Image from "next/image";
import api, { freelancerJobAPI } from "@/config/api";
import { API_BASE_URL } from '@/config/api';
import PhoneVerificationModal from "../../registration/Header/components/PhoneVerificationModal";
import LogoutModal from "@/components/LogoutModal/LogoutModal";
import UserNotificationDropdown from "@/components/UserNotificationDropdown";
import { createPortal } from "react-dom";
import { capitalizeFirst, capitalizeWords } from "@/lib/utils";

// User Profile Component - Extracted for cleaner code
const UserProfileButton = ({ user, badge, slug, isOnline, showUserDropdown, onToggleDropdown }) => {
  const getBadgeColors = (badgeName) => {
    if (!badgeName) return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', circleBorder: 'border-yellow-400' };
    const nameLower = badgeName.toLowerCase();
    if (nameLower.includes('platinum') || nameLower.includes('platinium')) {
      return { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', circleBorder: 'border-gray-400' };
    } else if (nameLower.includes('gold')) {
      return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', circleBorder: 'border-yellow-500' };
    } else if (nameLower.includes('silver')) {
      return { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-700', circleBorder: 'border-slate-400' };
    }
    return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', circleBorder: 'border-yellow-400' };
  };

  const getUserDisplayName = () => {
    return user?.name || user?.username || user?.slug || slug || `User${user?.id}` || "User";
  };

  const badgeNameStr = typeof badge === 'string' ? badge : badge?.name;
  const badgeIconStr = typeof badge === 'object' && badge !== null ? badge.icon : null;
  const colors = getBadgeColors(badgeNameStr);

  return (
    <button
      onClick={onToggleDropdown}
      className="flex items-center gap-2 lg:gap-2.5 px-2.5 lg:px-3 py-2 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 border border-transparent hover:border-gray-200 touch-manipulation group"
      aria-expanded={showUserDropdown}
      aria-label="User menu"
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center group-hover:border-gray-300 transition-colors">
          <User className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600" />
        </div>
        {/* Online Status Indicator for Freelancers */}
        {isOnline && (user?.user_type === "Freelancer" || user?.user_type === "freelancer") && (
          <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
        )}
      </div>

      {/* User Info - Desktop Only */}
      <div className="hidden xl:flex flex-col items-start min-w-0 max-w-[180px]">
        <div className="flex items-center gap-1.5 w-full">
          <span className="text-sm font-medium text-gray-900 truncate">
            {capitalizeWords(getUserDisplayName())}
          </span>
          {badgeIconStr && (
            <div className={`flex-shrink-0 w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-white border ${colors.circleBorder} shadow-sm`}>
              <img
                src={`${API_BASE_URL.replace('/api', '')}/${badgeIconStr}`}
                alt={badgeNameStr}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
          )}
        </div>
        {badgeNameStr && (
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.border} border ${colors.text} mt-0.5`}>
            {capitalizeWords(badgeNameStr)}
          </span>
        )}
      </div>

      {/* Dropdown Arrow */}
      <ChevronDown
        className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${showUserDropdown ? "rotate-180" : ""}`}
      />
    </button>
  );
};

// Mobile User Info Component
const MobileUserInfo = ({ user, badge, slug }) => {
  const getBadgeColors = (badgeName) => {
    if (!badgeName) return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', circleBorder: 'border-yellow-400' };
    const nameLower = badgeName.toLowerCase();
    if (nameLower.includes('platinum') || nameLower.includes('platinium')) {
      return { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', circleBorder: 'border-gray-400' };
    } else if (nameLower.includes('gold')) {
      return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', circleBorder: 'border-yellow-500' };
    } else if (nameLower.includes('silver')) {
      return { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-700', circleBorder: 'border-slate-400' };
    }
    return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', circleBorder: 'border-yellow-400' };
  };

  const getUserDisplayName = () => {
    return user?.name || user?.username || user?.slug || slug || `User${user?.id}` || "User";
  };

  const badgeNameStr = typeof badge === 'string' ? badge : badge?.name;
  const badgeIconStr = typeof badge === 'object' && badge !== null ? badge.icon : null;
  const colors = getBadgeColors(badgeNameStr);

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center shadow-sm">
          <User className="w-6 h-6 text-gray-600" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-gray-900 truncate text-base">
            {capitalizeWords(getUserDisplayName())}
          </p>
          {badgeIconStr && (
            <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-white border ${colors.circleBorder} shadow-sm`}>
              <img
                src={`${API_BASE_URL.replace('/api', '')}/${badgeIconStr}`}
                alt={badgeNameStr}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-sm text-gray-600">{capitalizeWords(user?.user_type)}</span>
          {badgeNameStr && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.border} border ${colors.text}`}>
              {capitalizeWords(badgeNameStr)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Default category icons mapping
const defaultCategoryIcons = {
  "Web Development": <Layers className="w-5 h-5" />,
  "Design": <Brush className="w-5 h-5" />,
  "Marketing": <Megaphone className="w-5 h-5" />,
  "Photography": <Camera className="w-5 h-5" />,
  "Writing": <ImageIcon className="w-5 h-5" />,
};

const Header = ({ params }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [mobileExpandedSubcategory, setMobileExpandedSubcategory] = useState(null);
  const [isTabletView, setIsTabletView] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const categoryDropdownRef = useRef(null);
  const categoryTriggerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const { user, slug, token, isAuthenticated, isOnline, badge, userType, is_active } = useSelector(state => state.auth);

  // CURSOR:HEADER_CHANGE_HERE - Stable user properties to prevent infinite loops
  const userId = user?.id;
  const userTypeStable = user?.user_type || userType;

  // Stable guard: only show full user menu features when user has user_type
  const showUserMenu = useMemo(() => Boolean(user && userTypeStable), [user, userTypeStable]);

  // Check if account is deactivated
  const accountDeactivated = useMemo(() => {
    return is_active === "0" || is_active === 0 || user?.is_active === "0" || user?.is_active === 0;
  }, [is_active, user?.is_active]);

  // Track if message count has been fetched to prevent re-triggering
  const hasFetchedMessageCountRef = useRef(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Fetch message count - only when user has user_type to prevent errors
  const fetchMessageCount = async () => {
    // Only fetch if user is authenticated AND has user_type
    if (!isAuthenticated || !showUserMenu || !userId) return;

    try {
      const response = await api.get('/msgCount');
      const count = response.data?.ch_count || 0;
      setMessageCount(count);
      hasFetchedMessageCountRef.current = true;
    } catch (error) {
      console.error('Error fetching message count:', error);
      setMessageCount(0);
    }
  };

  // Only fetch message count when user has user_type (stable dependencies)
  useEffect(() => {
    if (isAuthenticated && showUserMenu && userId) {
      // Fetch immediately and set up interval
      fetchMessageCount();
      const interval = setInterval(fetchMessageCount, 30000);
      return () => clearInterval(interval);
    } else if (!showUserMenu) {
      // Reset message count if user loses user_type
      setMessageCount(0);
      hasFetchedMessageCountRef.current = false;
    }
  }, [isAuthenticated, showUserMenu, userId]); // Use stable dependencies

  // Update user profile from localStorage if needed - use stable dependencies
  useEffect(() => {
    if (token && userId && (!user?.name || !user?.email || !user?.user_type)) {
      const rawUserData = localStorage.getItem('user');
      if (rawUserData) {
        try {
          const parsedUser = JSON.parse(rawUserData);
          dispatch(updateUserProfile(parsedUser));
        } catch (error) {
          console.log("Error parsing user data:", error);
        }
      }
    }
  }, [token, userId, user?.name, user?.email, user?.user_type, dispatch]); // Use stable dependencies

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get("/getSiteCategory");
        const scRes = response?.data?.sc_res || [];

        const transformedSubcategories = scRes
          .filter((entry) => entry?.sc_data)
          .map((entry) => {
            const category = entry.sc_data;
            const services = Array.isArray(entry.se_res) ? entry.se_res : [];

            return {
              id: category?.sc_id?.toString?.() || String(category?.sc_id ?? ""),
              title: category?.sc_name ?? "",
              slug: category?.sc_slug ?? "",
              items: services
                .filter((service) => service.se_id !== 1 && service.se_name?.toLowerCase() !== 'other')
                .map((service) => ({
                  id: service.se_id,
                  name: service.se_name,
                  slug: service.se_slug,
                })),
            };
          })
          .filter((category) => category.items.length > 0); // Remove categories with no services after filtering

        setSubcategories(transformedSubcategories);
        if (transformedSubcategories.length > 0) {
          setSelectedSubcategory(transformedSubcategories[0]);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsTabletView(window.innerWidth <= 1280);
      if (window.innerWidth > 1280) {
        setIsMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside category dropdown or trigger
      // Use both contains and closest for better detection
      const clickedInsideCategoryDropdown =
        categoryDropdownRef.current?.contains(event.target) ||
        event.target.closest(".category-dropdown");
      const clickedOnCategoryTrigger =
        categoryTriggerRef.current?.contains(event.target) ||
        event.target.closest(".categories-trigger");

      // Only close if click is truly outside both dropdown and trigger
      if (!clickedInsideCategoryDropdown && !clickedOnCategoryTrigger) {
        setShowDropdown(false);
      }

      // Check if click is outside user dropdown
      if (!event.target.closest(".user-dropdown")) {
        setShowUserDropdown(false);
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        setShowDropdown(false);
        setShowUserDropdown(false);
      }
    };

    const handleScrollClose = () => setShowDropdown(false);

    // Use mousedown instead of click to avoid interfering with link clicks
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeydown);
    window.addEventListener("scroll", handleScrollClose, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeydown);
      window.removeEventListener("scroll", handleScrollClose);
    };
  }, []);

  useEffect(() => {
    setShowDropdown(false);
    setShowUserDropdown(false);
  }, [pathname]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
      document.body.style.overflowX = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.overflowX = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
      document.body.style.overflowX = 'unset';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleOutsideClick = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isMobileMenuOpen]);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && !isMobileMenuOpen) {
      setIsMobileMenuOpen(true);
    }

    if (isRightSwipe && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  const toggleMobileCategoryDropdown = () => {
    setMobileCategoryOpen(!mobileCategoryOpen);
  };

  const toggleMobileSubcategory = (subcategoryId) => {
    setMobileExpandedSubcategory(mobileExpandedSubcategory === subcategoryId ? null : subcategoryId);
  };

  // Check if user is a customer - use stable userTypeStable
  const isCustomer = useMemo(() => {
    if (!userTypeStable) return false;
    const type = userTypeStable.toLowerCase();
    return type === "customer";
  }, [userTypeStable]);

  const navigationItems = [
    {
      name: "Explore Hyrelancer",
      href: "/about-us",
      icon: <Image src={Explor} alt="Explore" width={20} height={20} />,
    },
    {
      name: isCustomer ? "For Job Provider" : "For Freelancer",
      href: isCustomer ? "/customer-workflow" : "/freelancer-workflow",
      icon: <Image src={Service} alt="Service" width={20} height={20} />,
    },
    {
      name: "Contact Us",
      href: "/contact-us",
      icon: <Image src={Contact} alt="Contact" width={20} height={20} />,
    },
  ];


  // Show user dropdown only when authenticated and not on auth pages
  // Note: We allow showing dropdown even without user_type (for fallback UI)
  const shouldShowUserDropdown =
    isAuthenticated &&
    pathname !== "/login" &&
    pathname !== "/Register" &&
    pathname !== "/register";

  const shouldShowLoginButton = !shouldShowUserDropdown &&
    pathname !== "/login" &&
    pathname !== "/Register" &&
    pathname !== "/register";

  const isMobileVerified = user?.mobile_verified_at && user.mobile_verified_at !== null;

  const handleVerifyPhone = () => {
    setShowVerificationModal(true);
  };

  const handleVerificationSuccess = () => {
    dispatch(mobileVerified({ timestamp: new Date().toISOString() }));
    setShowVerificationModal(false);
  };

  const handleMessageClick = () => {
    // Only navigate if user has user_type
    if (!showUserMenu || !userTypeStable) {
      // If no user_type, redirect to select user type page
      router.push("/select-user-type");
      return;
    }

    const type = userTypeStable.toLowerCase();
    if (type === "customer") {
      router.push("/customer-dashboard/message");
    } else if (type === "freelancer") {
      router.push("/freelancer-dashboard/message");
    }
  };

  const handleOnlineToggle = async () => {
    try {
      const newStatus = !isOnline;
      const userId = user?.id;

      if (!userId) {
        console.error("User ID not found");
        return;
      }

      dispatch(updateOnlineStatus(newStatus));
      await freelancerJobAPI.updateAvailability(userId, newStatus);
    } catch (error) {
      console.error("Error updating availability status:", error);
      dispatch(updateOnlineStatus(!newStatus));
    }
  };

  // Check if user is a freelancer - use stable userTypeStable
  const isFreelancer = useMemo(() => {
    if (!userTypeStable) return false;
    const type = userTypeStable.toLowerCase();
    return type === "freelancer";
  }, [userTypeStable]);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setShowUserDropdown(false);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      await api.post("/logout");
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      dispatch(logout());
      setShowLogoutModal(false);
      setIsLoggingOut(false);
      router.push("/login");
    }
  };

  const getDashboardPath = () => {
    if (!userTypeStable) return "/select-user-type";
    const type = userTypeStable.toLowerCase();
    if (type === "customer") {
      return "/customer-dashboard";
    } else if (type === "freelancer") {
      return "/freelancer-dashboard";
    }
    return "/";
  };

  const getUserMenuItems = () => {
    const baseItems = [];

    // If user doesn't have user_type, show "Complete Profile" CTA instead of dashboard
    if (!showUserMenu) {
      baseItems.push({
        name: "Complete Profile",
        icon: <Settings className="w-4 h-4" />,
        onClick: () => {
          router.push("/select-user-type");
          setShowUserDropdown(false);
        },
      });
      baseItems.push({
        name: "Logout",
        icon: <LogOut className="w-4 h-4" />,
        onClick: handleLogoutClick,
      });
      return baseItems;
    }

    // User has user_type - show full menu
    baseItems.push({
      name: "Dashboard",
      icon: <Layers className="w-4 h-4" />,
      onClick: () => {
        const path = getDashboardPath();
        window.open(path, '_blank', 'noopener,noreferrer');
        setShowUserDropdown(false);
      },
    });

    // Add "Post New Job" for customers only, below Dashboard (only if account is active)
    if (isCustomer && !accountDeactivated) {
      baseItems.push({
        name: "Post New Job",
        icon: <FilePlus className="w-4 h-4" />,
        onClick: () => {
          window.open('/customer-dashboard/job-post', '_blank', 'noopener,noreferrer');
          setShowUserDropdown(false);
        },
      });
    }

    // Add "My Profile" for freelancers only (only if account is active)
    if (!isCustomer && !accountDeactivated) {
      baseItems.push({
        name: "My Profile",
        icon: <Settings className="w-4 h-4" />,
        onClick: () => {
          const profileSlug = slug || user?.slug;
          window.open(`/freelancer-profile/${profileSlug}`, '_blank', 'noopener,noreferrer');
          setShowUserDropdown(false);
        },
      });
    }

    baseItems.push({
      name: "Logout",
      icon: <LogOut className="w-4 h-4" />,
      onClick: handleLogoutClick,
    });

    return baseItems;
  };

  const userMenuItems = getUserMenuItems();

  return (
    <header className={`flex w-full h-12 sm:h-16 md:h-24 items-center justify-between px-2 sm:px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-24 py-3 sm:py-4 md:py-6 fixed top-0 z-50 transition-all duration-200 ${isScrolled ? 'bg-white/95 shadow-sm' : 'bg-[#ffffff80]'} backdrop-blur-xl border border-solid border-[#ffffff1a]`}>
      <div className="flex justify-between items-center w-full max-w-full xl:max-w-[1440px] mx-auto">
        {/* Left side - Logo - Aligned left on all devices */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
          {/* Desktop Logo */}
          <a
            href="/"
            aria-label="Hyrelancer Home"
            onClick={(e) => {
              e.preventDefault();
              router.push("/");
              setIsMobileMenuOpen(false);
            }}
            className="cursor-pointer flex-shrink-0 hidden lg:block"
          >
            <Image
              src={Logo}
              alt="Hyrelancer Logo"
              className="relative w-46 xl:w-40 h-auto"
              priority
            />
          </a>

          {/* Mobile Logo */}
          <a
            href="/"
            aria-label="Hyrelancer Home"
            onClick={(e) => {
              e.preventDefault();
              router.push("/");
              setIsMobileMenuOpen(false);
            }}
            className="cursor-pointer flex-shrink-0 block lg:hidden"
          >
            <Image
              src={Logo}
              alt="Hyrelancer Logo Mobile"
              className="relative w-28 sm:w-40 h-auto"
              priority
            />
          </a>
        </div>

        {/* Center - Navigation - All items move together to sidebar at xl breakpoint */}
        <nav
          className="hidden xl:flex items-center gap-4 xl:gap-6 2xl:gap-8"
          aria-label="Main navigation"
        >
          {/* Categories button - moves to sidebar with other nav items */}
          <button
            ref={categoryTriggerRef}
            onClick={() => setShowDropdown((prev) => !prev)}
            aria-expanded={showDropdown}
            className={`categories-trigger inline-flex h-10 lg:h-12 items-center gap-2 px-3 lg:px-4 xl:px-6 rounded-[32px] cursor-pointer transition-colors touch-manipulation ${showDropdown
              ? "bg-[#3a599c2e] hover:bg-[#3a599c40] active:bg-[#3a599c50]"
              : "bg-transparent hover:bg-gray-50 active:bg-gray-100"
              }`}
            aria-label="Categories menu"
          >
            <Image
              src={Catagery}
              alt="Categories"
              width={16}
              height={16}
              className="w-4 h-4 object-contain"
            />
            <span className={`font-normal text-xs lg:text-sm whitespace-nowrap transition-colors ${showDropdown
              ? "text-[#3a599c] font-semibold"
              : "text-black hover:text-[#3a599c]"
              }`}>
              Categories
            </span>
          </button>

          {/* Navigation items - all move together to sidebar */}
          {navigationItems.map((item, index) => {
            const isActive = pathname === item.href;
            return (
              <a
                key={index}
                href={item.href || "#"}
                onClick={(e) => {
                  if (item.href && item.href !== "#") {
                    e.preventDefault();
                    router.push(item.href);
                  }
                }}
                className={`font-normal text-xs lg:text-sm whitespace-nowrap transition-colors px-3 py-2 rounded-full touch-manipulation flex items-center gap-2 ${isActive
                  ? "text-[#3a599c] bg-[#3a599c15] font-semibold"
                  : "text-black hover:text-[#3a599c] hover:bg-gray-50 active:bg-gray-100"
                  }`}
              >
                {item.icon}
                {capitalizeWords(item.name)}
              </a>
            );
          })}
        </nav>

        {/* Right side - User actions */}
        <div className="flex items-center gap-1 xl:gap-2 2xl:gap-3">
          {/* Desktop User Actions - All move together to sidebar at lg breakpoint */}
          <div className="hidden lg:flex items-center gap-2">
            {shouldShowUserDropdown && (
              <div className="flex items-center gap-2">
                {/* Only show messages button if user has user_type and account is active */}
                {showUserMenu && !accountDeactivated && (
                  <button
                    onClick={handleMessageClick}
                    className="relative p-2 rounded-lg transition-colors duration-200 hover:bg-gray-100 active:bg-gray-200 text-gray-700 touch-manipulation"
                    title="Messages"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {messageCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {messageCount > 9 ? '9+' : messageCount}
                      </span>
                    )}
                  </button>
                )}

                {/* Only show notifications if user has user_type and account is active */}
                {showUserMenu && !accountDeactivated && (
                  <UserNotificationDropdown userType={userTypeStable?.toLowerCase()} />
                )}

                {/* Online/Offline button - moves with other user actions */}
                {showUserMenu && isFreelancer && !accountDeactivated && (
                  <div className="relative group">
                    <button
                      onClick={handleOnlineToggle}
                      className={`flex items-center justify-center w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200 touch-manipulation ${isOnline
                        ? "bg-green-50 text-green-600 hover:bg-green-100 active:bg-green-200 border border-green-200"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300 border border-gray-200"
                        }`}
                      title={isOnline ? "Online" : "Offline"}
                    >
                      {isOnline ? (
                        <Wifi className="w-5 h-5" />
                      ) : (
                        <WifiOff className="w-5 h-5" />
                      )}
                    </button>
                    <div className="absolute right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                        <div className="font-medium mb-1">{isOnline ? "Online" : "Offline"}</div>
                        <div className="text-gray-300 text-[10px]">
                          {isOnline ? "Click to go offline" : "Click to go online"}
                        </div>
                        <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mobile Verification button - moves with other user actions */}
                {showUserMenu && !isMobileVerified && !accountDeactivated && (
                  <div className="relative group">
                    <button
                      onClick={handleVerifyPhone}
                      className="flex items-center justify-center w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200 bg-orange-50 text-orange-600 hover:bg-orange-100 active:bg-orange-200 border border-orange-200 touch-manipulation"
                      title="Verify Phone"
                    >
                      <Phone className="w-5 h-5 animate-vibrate scale-x-[-1]" />
                    </button>
                    <div className="absolute right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                        <div className="font-medium mb-1">Verify Phone</div>
                        <div className="text-gray-300 text-[10px]">
                          Click to verify your number
                        </div>
                        <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="relative user-dropdown">
                  <UserProfileButton
                    user={user}
                    badge={badge}
                    slug={slug}
                    isOnline={isOnline}
                    showUserDropdown={showUserDropdown}
                    onToggleDropdown={() => setShowUserDropdown(!showUserDropdown)}
                  />

                  {showUserDropdown && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 overflow-hidden">
                      {userMenuItems.map((item, index) => (
                        <button
                          key={item.name}
                          onClick={item.onClick}
                          className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left transition-colors duration-150 touch-manipulation ${index === userMenuItems.length - 1
                            ? "text-red-600 hover:bg-red-50 active:bg-red-100"
                            : "text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                            }`}
                        >
                          <span className={index === userMenuItems.length - 1 ? "text-red-600" : "text-gray-400"}>
                            {item.icon}
                          </span>
                          <span className="text-sm font-medium">{capitalizeWords(item.name)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {shouldShowLoginButton && (
              <a
                href="/login"
                onClick={(e) => {
                  e.preventDefault();
                  router.push("/login");
                }}
                className="inline-flex h-10 lg:h-12 items-center justify-center px-4 lg:px-6 rounded-3xl border-2 border-[#3a599c] hover:bg-[#3a599c0a] active:bg-[#3a599c15] transition-colors touch-manipulation"
              >
                <span className="font-semibold text-[#3a599c] text-sm whitespace-nowrap">
                  Login
                </span>
              </a>
            )}

            {!isAuthenticated && (
              <a
                href="/customer-workflow"
                onClick={(e) => {
                  e.preventDefault();
                  router.push("/customer-workflow");
                }}
                className="inline-flex h-10 lg:h-12 items-center justify-center px-4 lg:px-6 bg-[#3a599c] rounded-3xl hover:bg-[#2f4a7f] active:bg-[#1e3a5f] transition-colors touch-manipulation"
              >
                <span className="font-normal text-white text-sm whitespace-nowrap">
                  Become A Service Provider
                </span>
              </a>
            )}
          </div>

          {/* Mobile Menu Button - Shows when navigation items move to sidebar, positioned on the right */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="xl:hidden p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors text-gray-700 touch-manipulation"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <div className="relative w-6 h-6">
              <span className={`absolute top-1 left-0 w-6 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`absolute top-3 left-0 w-6 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`absolute top-5 left-0 w-6 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </div>
          </button>
        </div>
      </div>

      {/* Desktop Categories Dropdown */}
      {showDropdown && (
        <div
          ref={categoryDropdownRef}
          className="category-dropdown absolute z-40 top-full left-1/2 -translate-x-1/2 w-full max-w-[1440px]"
        >
          <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 2xl:px-24">
            <div
              className="overflow-hidden rounded-2xl lg:rounded-3xl bg-white shadow-[0_8px_30px_rgba(0,0,0,0.12)] mt-2"
              onMouseDown={(e) => {
                // Only stop propagation if clicking on white space (not on links/buttons)
                const target = e.target;
                const isClickable = target.closest('a, button, [role="button"]');
                if (!isClickable) {
                  e.stopPropagation();
                }
              }}
            >
              <div className="flex">
                <div className="w-[240px] lg:w-[280px] bg-gray-50 p-4 lg:p-6 border-r border-gray-200">
                  <div className="space-y-1 max-h-[400px] lg:max-h-[500px] overflow-y-auto">
                    {subcategories.map((sub) => {
                      const isSelected = selectedSubcategory?.id === sub.id;
                      return (
                        <button
                          key={sub.id}
                          onMouseEnter={() => setSelectedSubcategory(sub)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSubcategory(sub);
                          }}
                          className={`flex items-center gap-2 lg:gap-3 w-full px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-left transition-all touch-manipulation cursor-pointer ${isSelected
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-700 hover:bg-white/50 active:bg-white/70"
                            }`}
                        >
                          <span className={isSelected ? "text-[#3a599c]" : "text-gray-500"}>
                            <CheckCircle2 className="h-3 w-3 lg:h-4 lg:w-4" />
                          </span>
                          <span className="font-medium text-sm lg:text-[15px]">{capitalizeWords(sub.title)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex-1 p-4 lg:p-8 max-h-[400px] lg:max-h-[500px] overflow-y-auto">
                  {selectedSubcategory && (
                    <>
                      <div className="mb-4 lg:mb-6 pb-3 lg:pb-4 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                          <span className="text-[#3a599c]">
                            <CheckCircle2 className="h-3 w-3 lg:h-4 lg:w-4" />
                          </span>
                          <h2 className="text-base lg:text-lg font-semibold text-gray-900">
                            {capitalizeWords(selectedSubcategory.title)}
                          </h2>
                        </div>
                      </div>

                      <ul className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 lg:gap-x-12 gap-y-2 lg:gap-y-3">
                        {selectedSubcategory.items?.length > 0 ? (
                          selectedSubcategory.items.map((service) => {
                            const categorySlug = selectedSubcategory.slug || selectedSubcategory.id;
                            const serviceSlug = service.slug || service.id;
                            const categoryServicePath = `/categories/${categorySlug}/${serviceSlug}`;

                            return (
                              <li key={service.id}>
                                <a
                                  href={categoryServicePath}
                                  onMouseDown={(e) => {
                                    // Stop propagation to prevent outside handler from interfering
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowDropdown(false);
                                    router.push(categoryServicePath);
                                  }}
                                  className="flex items-start gap-2 text-xs lg:text-[14px] text-gray-600 hover:text-[#3a599c] transition-colors group touch-manipulation cursor-pointer"
                                >
                                  <CheckCircle2 className="h-3 w-3 lg:h-4 lg:w-4 text-[#3a599c] flex-shrink-0 mt-0.5 opacity-60 group-hover:opacity-100" />
                                  <span>{service.name}</span>
                                </a>
                              </li>
                            );
                          })
                        ) : (
                          <div className="col-span-2 text-center py-8 text-gray-500">
                            No services available
                          </div>
                        )}
                      </ul>
                    </>
                  )}
                </div>


                <div className="hidden lg:block w-[280px] xl:w-[320px] bg-gray-50 p-4 lg:p-6 pt-32 lg:pt-40 border-l border-gray-200">
                  <div className="sticky top-6">
                    <div className="bg-white rounded-xl lg:rounded-2xl overflow-hidden shadow-sm">
                      <Image
                        src={require('../../../../public/images/web-developer.jpg')}
                        alt="Premium services"
                        width={320}
                        height={192}
                        className="w-full h-[160px] lg:h-[192px] object-cover"
                      />
                    </div>
                    <div className="mt-3 lg:mt-4">
                      <p className="text-sm lg:text-[15px] font-semibold text-gray-900 mb-2 lg:mb-3">
                        Premium services curated for your needs
                      </p>
                      <button
                        onClick={() => {
                          router.push('/categories');
                          setShowDropdown(false);
                        }}
                        className="flex items-center gap-2 text-[#3a599c] font-semibold text-xs lg:text-[14px] hover:gap-3 transition-all group touch-manipulation"
                      >
                        <span>View All Categories</span>
                        <svg
                          className="h-3 w-3 lg:h-4 lg:w-4 group-hover:translate-x-1 transition-transform"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 8H15M15 8L8 1M15 8L8 15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 backdrop-blur-sm xl:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="fixed inset-y-0 right-0 z-40 w-80 max-w-[85vw] bg-white shadow-2xl xl:hidden overflow-x-hidden"
          style={{
            top: "0",
            height: "100vh",
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors text-gray-500"
              aria-label="Close menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="px-4 py-6 space-y-6">
              {/* Mobile Categories Dropdown */}
              <div className="space-y-3">
                <button
                  onClick={toggleMobileCategoryDropdown}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation"
                  aria-expanded={mobileCategoryOpen}
                >
                  <div className="flex items-center gap-3">
                    <Grid className="w-5 h-5 text-gray-600" />
                    <span className="font-semibold text-gray-900">Categories</span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${mobileCategoryOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {mobileCategoryOpen && (
                  <div className="mobile-category-scroll mobile-scroll-container space-y-2 pl-4 pr-3 py-2 max-h-[400px] overflow-y-auto rounded-lg border-2 border-gray-200 bg-gray-50 shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]">
                    {!loading && subcategories.map((subcategory) => (
                      <div key={subcategory.id} className="space-y-2">
                        <button
                          onClick={() => toggleMobileSubcategory(subcategory.id)}
                          className="w-full flex justify-between items-center px-4 py-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                          aria-expanded={mobileExpandedSubcategory === subcategory.id}
                        >
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-left">{capitalizeWords(subcategory.title)}</span>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 transition-transform duration-200 text-gray-400 ${mobileExpandedSubcategory === subcategory.id ? "rotate-180" : ""}`}
                          />
                        </button>

                        {mobileExpandedSubcategory === subcategory.id && (
                          <div className="pl-4 space-y-2">
                            <ul className="space-y-1">
                              {subcategory.items.length > 0 ? (
                                subcategory.items.slice(0, 10).map((item) => {
                                  const categorySlug = subcategory.slug || subcategory.id;
                                  const serviceSlug = item.slug || item.id;
                                  const categoryServicePath = `/categories/${categorySlug}/${serviceSlug}`;

                                  return (
                                    <li key={item.id}>
                                      <a
                                        href={categoryServicePath}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          router.push(categoryServicePath);
                                          setIsMobileMenuOpen(false);
                                        }}
                                        className="block py-2 px-3 text-sm text-gray-600 hover:text-[#3a599c] hover:bg-gray-50 rounded-lg transition-colors touch-manipulation"
                                      >
                                        {item.name}
                                      </a>
                                    </li>
                                  );
                                })
                              ) : (
                                <li className="block py-2 px-3 text-sm text-gray-400">
                                  No services available
                                </li>
                              )}
                              {subcategory.items.length > 10 && (
                                <li>
                                  <span className="block py-2 px-3 text-xs text-gray-400">
                                    +{subcategory.items.length - 10} more services
                                  </span>
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      onClick={() => {
                        router.push('/categories');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-3 text-[#3a599c] font-semibold text-sm border border-[#3a599c] rounded-lg hover:bg-[#3a599c0a] active:bg-[#3a599c15] transition-colors touch-manipulation"
                    >
                      <span>View All Categories</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Navigation Items */}
              <div className="space-y-2">
                <h3 className="font-bold text-lg text-gray-900 mb-3">Quick Links</h3>
                {navigationItems.map((item) => {
                  const isActive = pathname === item.href;
                  return (
                    <a
                      key={item.name}
                      href={item.href || "#"}
                      onClick={(e) => {
                        if (item.href && item.href !== "#") {
                          e.preventDefault();
                          router.push(item.href);
                          setIsMobileMenuOpen(false);
                        }
                      }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors touch-manipulation ${isActive
                        ? "text-[#3a599c] bg-[#3a599c15] font-semibold"
                        : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
                        }`}
                    >
                      {item.icon}
                      {capitalizeWords(item.name)}
                    </a>
                  );
                })}
              </div>

              {/* Mobile Buttons - Only show when NOT authenticated */}
              {!isAuthenticated && (
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      router.push("/customer-workflow");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-6 py-4 rounded-lg font-bold bg-[#3a599c] text-white hover:bg-[#2f4a7f] active:bg-[#1e3a5f] transition-colors touch-manipulation"
                  >
                    Become A Service Provider
                  </button>
                  <button
                    onClick={() => {
                      router.push("/login");
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-6 py-3 rounded-lg font-medium border border-[#3a599c] text-[#3a599c] hover:bg-[#3a599c0a] active:bg-[#3a599c15] transition-colors touch-manipulation"
                  >
                    Login
                  </button>
                </div>
              )}

              {/* Mobile Action Buttons - Only show if authenticated */}
              {shouldShowUserDropdown && (
                <div className="space-y-4">
                  <h3 className="font-bold text-lg text-gray-900">Account</h3>

                  {/* User Info */}
                  <MobileUserInfo user={user} badge={badge} slug={slug} />

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {/* If user doesn't have user_type, show "Complete Profile" instead of Dashboard */}
                    {!showUserMenu ? (
                      <button
                        onClick={() => {
                          router.push("/select-user-type");
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors touch-manipulation border border-blue-200"
                      >
                        <Settings className="w-5 h-5 text-blue-600" />
                        <span className="font-medium">Complete Profile</span>
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            const path = getDashboardPath();
                            window.open(path, '_blank', 'noopener,noreferrer');
                            setIsMobileMenuOpen(false);
                          }}
                          className="flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                        >
                          <Layers className="w-5 h-5 text-gray-500" />
                          <span className="font-medium">Dashboard</span>
                        </button>

                        {/* Post New Job - Only for customers */}
                        {isCustomer && !accountDeactivated && (
                          <button
                            onClick={() => {
                              window.open('/customer-dashboard/job-post', '_blank', 'noopener,noreferrer');
                              setIsMobileMenuOpen(false);
                            }}
                            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                          >
                            <FilePlus className="w-5 h-5 text-gray-500" />
                            <span className="font-medium">Post New Job</span>
                          </button>
                        )}

                        {!accountDeactivated && (
                          <button
                            onClick={() => {
                              handleMessageClick();
                              setIsMobileMenuOpen(false);
                            }}
                            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 text-gray-700 transition-colors touch-manipulation relative"
                          >
                            <MessageCircle className="w-5 h-5 text-gray-500" />
                            <span className="font-medium">Messages</span>
                            {messageCount > 0 && (
                              <span className="ml-auto w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                {messageCount > 9 ? '9+' : messageCount}
                              </span>
                            )}
                          </button>
                        )}

                        {!isCustomer && !accountDeactivated && (
                          <button
                            onClick={() => {
                              const profileSlug = slug || user?.slug;
                              window.open(`/freelancer-profile/${profileSlug}`, '_blank', 'noopener,noreferrer');
                              setIsMobileMenuOpen(false);
                            }}
                            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                          >
                            <Settings className="w-5 h-5 text-gray-500" />
                            <span className="font-medium">My Profile</span>
                          </button>
                        )}

                        {isFreelancer && !accountDeactivated && (
                          <button
                            onClick={() => {
                              handleOnlineToggle();
                              setIsMobileMenuOpen(false);
                            }}
                            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors touch-manipulation ${isOnline
                              ? "bg-green-50 text-green-700 hover:bg-green-100 active:bg-green-200 border border-green-200"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300 border border-gray-200"
                              }`}
                          >
                            {isOnline ? (
                              <Wifi className="w-5 h-5" />
                            ) : (
                              <WifiOff className="w-5 h-5" />
                            )}
                            <span className="font-medium">{isOnline ? "Online" : "Offline"}</span>
                          </button>
                        )}

                        {!isMobileVerified && !accountDeactivated && (
                          <button
                            onClick={() => {
                              handleVerifyPhone();
                              setIsMobileMenuOpen(false);
                            }}
                            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 active:bg-orange-200 transition-colors touch-manipulation border border-orange-200"
                          >
                            <Phone className="w-5 h-5 animate-vibrate scale-x-[-1]" />
                            <span className="font-medium">Verify Phone</span>
                          </button>
                        )}
                      </>
                    )}

                    <button
                      onClick={handleLogoutClick}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors touch-manipulation border border-red-200"
                    >
                      <LogOut className="w-5 h-5" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phone Verification Modal */}
      {isMounted && showVerificationModal && createPortal(
        <PhoneVerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onSuccess={handleVerificationSuccess}
        />,
        document.body
      )}

      {/* Logout Modal */}
      {isMounted && showLogoutModal && createPortal(
        <LogoutModal
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogoutConfirm}
          isLoading={isLoggingOut}
        />,
        document.body
      )}
    </header>
  );
};

export default Header;